import React, { useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Box,
  Chip,
  Paper,
  Stack,
  Tab,
  Tabs,
  Toolbar,
  Typography,
} from "@mui/material";
import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import WorkRoundedIcon from "@mui/icons-material/WorkRounded";
import ViewListRoundedIcon from "@mui/icons-material/ViewListRounded";
import PersonSearchRoundedIcon from "@mui/icons-material/PersonSearchRounded";
import HandshakeRoundedIcon from "@mui/icons-material/HandshakeRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";

import ProjectManagement from "../component/ProjectManagement";
import { designTokens } from "../theme";

type ProjectTab =
  | "all"
  | "prospective"
  | "existing"
  | "closed";

type ProjectTabDefinition = {
  value: ProjectTab;
  label: string;
  description: string;
  icon: React.ReactElement;
};

const projectTabs: ProjectTabDefinition[] = [
  {
    value: "all",
    label: "全案件",
    description:
      "見込み顧客・既存顧客を含むすべての案件を確認します。",
    icon: <ViewListRoundedIcon fontSize="small" />,
  },
  {
    value: "prospective",
    label: "見込み顧客",
    description:
      "見込み顧客に紐づく案件だけを確認します。",
    icon: <PersonSearchRoundedIcon fontSize="small" />,
  },
  {
    value: "existing",
    label: "既存顧客",
    description:
      "既存顧客に紐づく案件だけを確認します。",
    icon: <HandshakeRoundedIcon fontSize="small" />,
  },
  {
    value: "closed",
    label: "終了案件",
    description: "終了した案件を確認します。",
    icon: <HistoryRoundedIcon fontSize="small" />,
  },
];

const isProjectTab = (
  value: string | null,
): value is ProjectTab => {
  return projectTabs.some((tab) => tab.value === value);
};

export default function Projects() {
  const { isAuthenticated } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();

  const selectedTab = useMemo<ProjectTab>(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");

    return isProjectTab(tab) ? tab : "all";
  }, [location.search]);

  const selectedDefinition =
    projectTabs.find((tab) => tab.value === selectedTab) ??
    projectTabs[0];

  const handleTabChange = (
    _: React.SyntheticEvent,
    value: ProjectTab,
  ) => {
    navigate(`/projects?tab=${value}`, {
      replace: true,
    });
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        background:
          "radial-gradient(circle at top right, rgba(152,188,46,0.07), transparent 28%), #F4F6F8",
      }}
    >
      {/* 固定ヘッダーの高さ分、ページ全体を下げる */}
      <Toolbar />

      <Box
        sx={{
          px: { xs: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2 },
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 1480,
            mx: "auto",
          }}
        >
          {/* ページタイトル・説明・案件区分タブ */}
          <Paper
            elevation={0}
            sx={{
              mb: 1.5,
              borderRadius: 3,
              position: "relative",
              overflow: "hidden",
              background:
                "linear-gradient(115deg, rgba(255,255,255,0.98), rgba(247,250,242,0.98) 62%, rgba(235,243,225,0.98))",
              border: `1px solid ${designTokens.border.default}`,
              boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                opacity: 0.35,
                pointerEvents: "none",
                backgroundImage:
                  "radial-gradient(rgba(93,116,62,0.14) 0.7px, transparent 0.7px)",
                backgroundSize: "15px 15px",
                maskImage:
                  "linear-gradient(90deg, rgba(0,0,0,0.65), transparent 60%)",
                WebkitMaskImage:
                  "linear-gradient(90deg, rgba(0,0,0,0.65), transparent 60%)",
              },
            }}
          >
            {/* タイトル部分 */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={1.5}
              sx={{
                position: "relative",
                zIndex: 1,
                p: { xs: 1.6, md: 2 },
              }}
            >
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="center"
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                    borderRadius: 2,
                    color: "#6B5FC7",
                    backgroundColor: "rgba(107,95,199,0.09)",
                    border: "1px solid rgba(107,95,199,0.16)",
                  }}
                >
                  <WorkRoundedIcon />
                </Box>

                <Box>
                  <Typography
                    component="h1"
                    sx={{
                      color: designTokens.text.primary,
                      fontSize: { xs: 20, md: 24 },
                      fontWeight: 900,
                    }}
                  >
                    案件管理
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.25,
                      color: designTokens.text.secondary,
                      fontSize: 13,
                    }}
                  >
                    {selectedDefinition.description}
                  </Typography>
                </Box>
              </Stack>

              <Chip
                size="small"
                label={selectedDefinition.label}
                sx={{
                  fontWeight: 700,
                  color: "#6B5FC7",
                  backgroundColor: "rgba(107,95,199,0.09)",
                  border: "1px solid rgba(107,95,199,0.16)",
                }}
              />
            </Stack>

            {/* 案件区分タブ */}
            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                borderTop: `1px solid ${designTokens.border.default}`,
                backgroundColor: "rgba(255,255,255,0.72)",
                overflowX: "auto",
              }}
            >
              <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                aria-label="案件区分"
                sx={{
                  minHeight: 56,
                  px: { xs: 0.5, md: 1 },
                  "& .MuiTabs-indicator": {
                    height: 3,
                    borderRadius: "3px 3px 0 0",
                    backgroundColor: "#6B5FC7",
                  },
                  "& .MuiTab-root": {
                    minHeight: 56,
                    minWidth: { xs: 140, sm: 170 },
                    color: designTokens.text.secondary,
                    fontWeight: 800,
                    textTransform: "none",
                    borderRadius: 2,
                    mx: 0.25,
                    "&:hover": {
                      color: "#6B5FC7",
                      backgroundColor: "rgba(107,95,199,0.08)",
                    },
                    "&.Mui-selected": {
                      color: "#6B5FC7",
                    },
                  },
                }}
              >
                {projectTabs.map((tab) => (
                  <Tab
                    key={tab.value}
                    value={tab.value}
                    icon={tab.icon}
                    iconPosition="start"
                    label={tab.label}
                  />
                ))}
              </Tabs>
            </Box>
          </Paper>

          {/* 案件一覧 */}
          <Box
            sx={{
              minHeight: 0,
              "& > *": {
                maxWidth: "none",
              },
            }}
          >
            <ProjectManagement
              key={selectedTab}
              customerType={selectedTab === "closed" ? undefined : selectedTab}
              projectStatus={
                selectedTab === "closed"
                  ? "closed"
                  : selectedTab === "prospective" || selectedTab === "existing"
                    ? "active"
                    : "all"
              }
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
